/**
 * Domain configuration for multi-domain deployment
 *
 * www.framed.dev - Marketing site (homepage, trial, pricing, checkout)
 * app.framed.dev - Application (dashboard, wizard, settings, admin)
 *
 * Set VITE_USE_RELATIVE_URLS=true to use relative paths (for pre-domain setup)
 */

// Environment-based configuration
export const WWW_DOMAIN = import.meta.env.VITE_WWW_DOMAIN || 'https://www.framed.dev'
export const APP_DOMAIN = import.meta.env.VITE_APP_DOMAIN || 'https://app.framed.dev'
export const APP_MODE = (import.meta.env.VITE_APP_MODE || 'app') as 'www' | 'app'

// Use relative URLs until custom domains are set up
// In production, always use absolute URLs for cross-domain links
// Only use relative URLs in development or if explicitly set
const USE_RELATIVE_URLS = import.meta.env.VITE_USE_RELATIVE_URLS === 'true' || import.meta.env.DEV

// Check which mode we're in
export const isWwwMode = APP_MODE === 'www'
export const isAppMode = APP_MODE === 'app'

// URL helpers for cross-domain navigation
export const getLoginUrl = () => `${APP_DOMAIN}/login`
export const getDashboardUrl = () => `${APP_DOMAIN}/dashboard`
export const getPricingUrl = () => `${WWW_DOMAIN}/pricing`
export const getHomeUrl = () => WWW_DOMAIN
export const getTryUrl = () => `${WWW_DOMAIN}/try`

// Helper to check if we should use relative URLs
// Only use relative URLs in development on localhost
const shouldUseRelativeUrls = () => {
  if (typeof window === 'undefined') return true
  return USE_RELATIVE_URLS || window.location.hostname === 'localhost'
}

// Cross-domain links: Always use absolute URLs when navigating between www and app domains
// These functions ensure login/dashboard links from www go to app, and vice versa

export const getLoginLink = () => {
  // Login lives on app domain - always use absolute URL when on www
  if (isWwwMode || (typeof window !== 'undefined' && window.location.hostname.startsWith('www.'))) {
    return getLoginUrl()
  }
  if (shouldUseRelativeUrls()) {
    return '/login'
  }
  return getLoginUrl()
}

export const getDashboardLink = () => {
  // Dashboard lives on app domain - always use absolute URL when on www
  if (isWwwMode || (typeof window !== 'undefined' && window.location.hostname.startsWith('www.'))) {
    return getDashboardUrl()
  }
  if (shouldUseRelativeUrls()) {
    return '/dashboard'
  }
  return getDashboardUrl()
}

export const getPricingLink = () => {
  // Pricing lives on www domain - always use absolute URL when on app
  if (isAppMode && !shouldUseRelativeUrls()) {
    return getPricingUrl()
  }
  if (shouldUseRelativeUrls()) {
    return '/pricing'
  }
  return getPricingUrl()
}

export const getHomeLink = () => {
  // Home lives on www domain - always use absolute URL when on app
  if (isAppMode && !shouldUseRelativeUrls()) {
    return getHomeUrl()
  }
  if (shouldUseRelativeUrls()) {
    return '/'
  }
  return getHomeUrl()
}

export const getTryLink = () => {
  // Try lives on www domain - always use absolute URL when on app
  if (isAppMode && !shouldUseRelativeUrls()) {
    return getTryUrl()
  }
  if (shouldUseRelativeUrls()) {
    return '/try'
  }
  return getTryUrl()
}

// Signup URL helpers - signup lives on app domain
export const getSignupUrl = (params?: { tier?: string; pack?: string }) => {
  const base = `${APP_DOMAIN}/signup`
  if (!params) return base
  const searchParams = new URLSearchParams()
  if (params.tier) searchParams.set('tier', params.tier)
  if (params.pack) searchParams.set('pack', params.pack)
  const qs = searchParams.toString()
  return qs ? `${base}?${qs}` : base
}

export const getSignupLink = (params?: { tier?: string; pack?: string }) => {
  // Signup lives on app domain - always use absolute URL when on www
  if (isWwwMode || (typeof window !== 'undefined' && window.location.hostname.startsWith('www.'))) {
    return getSignupUrl(params)
  }
  if (shouldUseRelativeUrls()) {
    const base = '/signup'
    if (!params) return base
    const searchParams = new URLSearchParams()
    if (params.tier) searchParams.set('tier', params.tier)
    if (params.pack) searchParams.set('pack', params.pack)
    const qs = searchParams.toString()
    return qs ? `${base}?${qs}` : base
  }
  return getSignupUrl(params)
}
